import { FC, useState } from 'react';
import { Box, Skeleton, Svg, Text } from '@hopskipdrive/react-components';
import { endOfDay, formatISO } from 'date-fns';

import { Pagination } from '../../../shared/components/Pagination';
import { formatInTimezone } from '../../../shared/utils/helpers/date';
import { estimatedTimeFromNowWithin24Hours } from '../../../shared/utils/helpers/util';
import { useRidesQuery } from '../../hooks/useRidesQuery';

import { RIDES_QUERY_PARAMS } from './constants';
import { SameDayRide } from './UpcomingRides.style';

interface UpcomingRidesProps {
  userSlug?: string;
  riderAssistantId?: string;
}

export const UpcomingRides: FC<UpcomingRidesProps> = ({ userSlug, riderAssistantId }) => {
  const now = new Date();
  const endOfToday = endOfDay(now);
  const [pageNumber, setPageNumber] = useState<number>(RIDES_QUERY_PARAMS.page);
  const { data: upcomingRidesData, isFetching } = useRidesQuery({
    view: 'slim',
    perPage: RIDES_QUERY_PARAMS.perPage,
    page: pageNumber,
    states: ['unclaimed', 'claimed'],
    startDatetime: formatISO(now),
    endDatetime: formatISO(endOfToday),
    ...(!riderAssistantId ? { userId: userSlug } : { riderAssistantId: riderAssistantId }),
  });

  const rideCount = upcomingRidesData?.meta?.totalCount || 0;
  const perPage = upcomingRidesData?.meta?.perPage || 1;
  const pageCount = Math.ceil(rideCount / perPage);

  return (
    <>
      <Box gap="5.52px" display="flex" alignItems="center">
        <Svg icon="upcoming_rides" width="24px" height="24px" />
        <Text variant="h6">Upcoming Same-Day Rides</Text>
      </Box>
      <ListOfRides
        isFetching={isFetching}
        upcomingRides={upcomingRidesData?.data}
        pageCount={pageCount}
        onPageChange={(_, page) => setPageNumber(page)}
      />
    </>
  );
};

// To facilitate testing, we can export the unconnected component as well
export const ListOfRides = ({ isFetching, upcomingRides, pageCount, onPageChange }) => {
  return (
    <Box display="flex" flexDirection="column" gap="4px">
      {isFetching ? (
        <SkeletonRides />
      ) : (
        upcomingRides?.map((ride, index) => (
          <SameDayRide key={index}>
            <Text variant="body1" textColor="secondaryText">
              {formatInTimezone(ride.startsAt, ride.timeZone, "EEE, M/dd/yyyy 'at' h:mm aa zzz")}
              {estimatedTimeFromNowWithin24Hours(ride.startsAt)}
            </Text>
          </SameDayRide>
        ))
      )}
      {pageCount > 1 && <Pagination count={pageCount} siblingCount={3} onChange={onPageChange} />}
    </Box>
  );
};

export const SkeletonRides = () => (
  <>
    <SameDayRide>
      <Skeleton data-testid="ride-skeleton" variant="text" width={400} height={24} />
    </SameDayRide>
    <SameDayRide>
      <Skeleton data-testid="ride-skeleton" variant="text" width={400} height={24} />
    </SameDayRide>
    <SameDayRide>
      <Skeleton data-testid="ride-skeleton" variant="text" width={400} height={24} />
    </SameDayRide>
    <SameDayRide>
      <Skeleton data-testid="ride-skeleton" variant="text" width={400} height={24} />
    </SameDayRide>
  </>
);
